// src/frontend/data/books/DarkForest/data/cap9.js

const CAP9 = {
  id: 9,
  title: "Cifrado",
  sections: [
    {
      heading: "¿Qué entendemos por cifrado?",
      text: [
        "El cifrado es un concepto muy, muy amplio. No importa si hablamos de cifrado simétrico, asimétrico o de técnicas criptográficas avanzadas: mientras un mensaje cifrado pueda ser descifrado de forma sencilla por ti (o por tu equipo de emergencia) incluso décadas después, pero nadie más pueda hacerlo, hablamos de un buen cifrado.",
        "En el contexto de las billeteras cripto, el cifrado es la capa extra que colocas encima de tu respaldo: aunque alguien consiga acceso físico a una copia, todavía tendría que superar una barrera matemática muy difícil de romper."
      ]
    },
    {
      heading: "Cifrado y el principio de confianza cero",
      text: [
        "De acuerdo con el principio de seguridad de confianza cero, cuando respaldas una billetera debes asumir que cualquier paso del proceso podría ser comprometido, incluyendo entornos físicos como una caja fuerte o una oficina cerrada con llave.",
        "La regla mental es dura pero útil: no hay nadie completamente confiable excepto tú mismo. Y, siendo honestos, a veces ni siquiera tú eres totalmente confiable, porque la memoria falla, las notas se extravían y las circunstancias cambian.",
        "Eso no significa que debas vivir en paranoia permanente, pero sí que necesitas diseñar tus respaldos con la idea de que nada es infalible: ni las personas, ni los dispositivos, ni los lugares."
      ]
    },
    {
      heading: "Cifrado y recuperación ante desastres",
      text: [
        "Cuando hablamos de cifrado en respaldos, no solo pensamos en hackers, sino también en recuperación ante desastres. El objetivo principal es evitar un único punto de fallo.",
        "Preguntas clave:",
        "🔹 ¿Qué pasaría si tú ya no estás disponible para recuperar la billetera?",
        "🔹 ¿Qué ocurriría si el lugar donde guardas el respaldo desaparece (incendio, inundación, robo, cierre de una caja bancaria)?",
        "Para activos importantes debe existir, como mínimo:",
        "🔹 Al menos una persona de recuperación o contacto de emergencia.",
        "🔹 Múltiples copias de respaldo en ubicaciones diferentes.",
        "La elección de esa persona o equipo de recuperación depende completamente de tu círculo de confianza y tu contexto legal/familiar, así que este libro no dicta quién debe ser. En cambio, nos enfocaremos en cómo diseñar el esquema de múltiples respaldos."
      ]
    },
    {
      heading: "Formas típicas de ubicar tus respaldos",
      text: [
        "En la práctica, la mayoría de los esquemas de cifrado + respaldo utilizan alguna combinación de estos cuatro lugares:",
        "🔹 Nube (Cloud).",
        "🔹 Papel.",
        "🔹 Dispositivos electrónicos.",
        "🔹 Cerebro (memoria).",
        "Cada uno tiene fortalezas y debilidades. Lo importante es entenderlas y no confiar ciegamente en un solo tipo de soporte."
      ]
    },
    {
      heading: "Respaldo cifrado en la nube",
      text: [
        "Muchas personas desconfían de la nube porque sienten que es un objetivo muy atractivo para atacantes. Y es cierto que los servicios en la nube pueden ser atacados.",
        "Al final, todo se reduce a una batalla entre atacantes y defensores: quién invierte más recursos, tiempo y talento. Personalmente, el texto original comenta confianza en proveedores grandes como Google, Apple o Microsoft, precisamente porque cuentan con equipos de seguridad muy fuertes y procesos serios de protección de datos.",
        "Aun así, nada es absoluto. Por eso, si decides usar la nube para respaldar información crítica (como una frase mnemotécnica o una clave privada), la regla de oro es: nunca subirla en texto plano; siempre cifrarla al menos una vez más antes de subirla."
      ],
      subsections: [
        {
          heading: "Uso de GPG para cifrar tus respaldos",
          text: [
            "Una de las herramientas más recomendadas en el mundo de la seguridad es GPG (GNU Privacy Guard). GPG sirve para dos cosas muy importantes:",
            "🔹 Verificar firmas criptográficas (por ejemplo, al descargar software).",
            "🔹 Cifrar y descifrar información de forma robusta.",
            "La idea general es:",
            "🔹 En un entorno seguro y sin conexión, tomas tu información sensible (frase mnemotécnica, clave privada, archivo de respaldo, etc.).",
            "🔹 La cifras con GPG usando tu llave pública o una contraseña fuerte.",
            "🔹 Subes únicamente el archivo cifrado a la nube (Google Drive, iCloud, OneDrive, etc.).",
            "De este modo, incluso si alguien obtiene una copia del archivo, no podrá hacer nada sin la llave privada de GPG o la contraseña que utilizaste.",
            "Advertencia importante: nunca pierdas la llave privada de GPG ni la contraseña asociada. Si las pierdes, ni tú mismo podrás descifrar tu propio respaldo."
          ]
        },
        {
          heading: "Gestores de contraseñas como apoyo",
          text: [
            "Aprender GPG, gestionar llaves y hacer copias cifradas requiere tiempo y práctica, pero una vez que dominas el proceso, deja de ser algo tan complicado.",
            "Si en algún momento quieres ahorrar esfuerzo, puedes apoyarte en gestores de contraseñas de buena reputación, como 1Password. Las versiones recientes permiten almacenar directamente información relacionada con wallets: frases mnemotécnicas, contraseñas, direcciones, etc.",
            "Otros gestores como Bitwarden ofrecen funcionalidades parecidas, aunque la experiencia de usuario puede variar. Eso sí, hay que tener en cuenta que este enfoque puede ser ligeramente menos robusto que tu propio esquema personalizado con GPG, porque delegas parte del modelo de seguridad en el proveedor del gestor.",
            "En cualquier caso, si usas gestores de contraseñas, protege tu cuenta con:",
            "🔹 Una contraseña maestra muy fuerte y única.",
            "🔹 Autenticación de dos factores (2FA) siempre que sea posible."
          ]
        }
      ]
    },
    {
      heading: "Respaldo en papel y soportes físicos",
      text: [
        "Muchas hardware wallets incluyen tarjetas de papel de buena calidad para que escribas tu frase mnemotécnica (en texto plano, en esquema Shamir, etc.). También existen placas de acero pensadas específicamente para grabar semillas de forma resistente al fuego y al agua.",
        "El flujo típico es:",
        "🔹 Escribir la frase mnemotécnica de forma clara y legible.",
        "🔹 Verificarla cuidadosamente (palabra por palabra).",
        "🔹 Probar la restauración si es posible, para asegurarte de que el respaldo es correcto.",
        "🔹 Guardarla en un lugar que consideres seguro (caja fuerte, caja bancaria, etc.).",
        "El texto original menciona una preferencia personal por el papel, porque si se almacena bien puede durar más que muchos dispositivos electrónicos. Sin embargo, es vulnerable a incendios, humedad, hongos y miradas indiscretas, por lo que casi siempre se combina con otros soportes (por ejemplo, papel + metal en lugares diferentes)."
      ]
    },
    {
      heading: "Respaldo en dispositivos electrónicos",
      text: [
        "En la categoría de dispositivos entran todo tipo de equipos electrónicos: computadoras, laptops, tablets, teléfonos, discos externos, etc. Mucha gente se siente cómoda guardando respaldos cifrados en estos dispositivos, sobre todo si ya los usan a diario.",
        "Puntos clave:",
        "🔹 Siempre asume que los dispositivos pueden fallar o quedar obsoletos en unos años.",
        "🔹 Procura usar canales relativamente seguros para transferir información sensible entre dispositivos, como cables USB o métodos punto a punto (por ejemplo, AirDrop).",
        "🔹 Evita enviar archivos sin cifrar por correo, mensajería o servicios desconocidos.",
        "Es recomendable revisar periódicamente que los dispositivos donde guardas respaldos siguen funcionando y que puedes acceder a los archivos cifrados. Esta revisión anual es una especie de “chequeo médico” para tu infraestructura de seguridad.",
        "Al igual que con la nube, lo ideal es que el archivo que guardes en el dispositivo ya esté cifrado con una herramienta robusta (como GPG) o con un gestor de contraseñas con buena reputación."
      ]
    },
    {
      heading: "Respaldo en el cerebro: el palacio de la memoria",
      text: [
        "El último tipo de respaldo es el más intangible: tu propia memoria. Algunas personas entrenan técnicas de memorización (palacio de la memoria, asociaciones, historias) para recordar frases mnemotécnicas o partes críticas de sus esquemas de seguridad.",
        "Usar la memoria tiene ventajas:",
        "🔹 No hay archivo físico que pueda ser robado.",
        "🔹 En ciertos contextos de alto riesgo, puede ser más seguro que llevar algo escrito.",
        "Pero también tiene riesgos muy serios:",
        "🔹 La memoria se degrada con el paso del tiempo, especialmente bajo estrés, enfermedad o cambios drásticos en la vida.",
        "🔹 Un accidente puede hacer imposible que recuerdes la información, o incluso que puedas comunicarla.",
        "Por eso, basar todo tu modelo de seguridad únicamente en la memoria suele ser mala idea. Puede ser un complemento interesante (por ejemplo, recordar solo la contraseña que acompaña a un respaldo físico), pero rara vez es suficiente como estrategia única."
      ]
    },
    {
      heading: "No te excedas con el cifrado",
      text: [
        "Existe un riesgo curioso pero real: cifrar demasiado. Es decir, agregar tantas capas y trucos a tu esquema de seguridad que, con el tiempo, tú mismo te conviertes en tu peor enemigo.",
        "Ejemplos de exceso de cifrado:",
        "🔹 Varias capas de contraseñas sin un registro claro de cuál se aplica a qué.",
        "🔹 Esquemas de división y recombinación que solo tú entiendes y que no has documentado en ninguna parte.",
        "🔹 Uso de herramientas que luego olvidas cómo funcionaban o dónde guardaste sus llaves.",
        "El objetivo del cifrado no es demostrar lo creativa o complejo que eres, sino encontrar un punto donde:",
        "🔹 Un atacante lo tenga extremadamente difícil.",
        "🔹 Tú puedas seguir usando y recuperando tus fondos con procedimientos claros, repetibles y verificados."
      ]
    },
    {
      heading: "Verificación continua de tus métodos",
      text: [
        "Siguiendo el principio de seguridad de verificación continua, cualquier método de cifrado y respaldo que diseñes debe ser sometido periódicamente a pruebas, tanto programadas como aleatorias.",
        "No siempre tienes que ejecutar la recuperación completa, pero sí conviene hacer pruebas parciales:",
        "🔹 Comprobar que sigues teniendo acceso a llaves de GPG y contraseñas.",
        "🔹 Verificar que puedes descifrar un archivo de prueba almacenado en la nube.",
        "🔹 Confirmar que las placas metálicas o el papel siguen legibles.",
        "🔹 Revisar que las rutas físicas (cajas fuertes, bancos, casas de familiares) siguen existiendo y accesibles.",
        "La frecuencia de estas verificaciones depende de tu memoria, de la criticidad de los fondos y de tu situación personal. Lo importante es que no se conviertan en algo que solo “planeas hacer algún día”.",
        "También es clave cuidar la confidencialidad del proceso de verificación: cuando hagas pruebas, evita exponer tu seed o tus claves en entornos inseguros, cámaras, pantallas compartidas o redes públicas."
      ]
    },
    {
      heading: "Listo para entrar al bosque oscuro",
      text: [
        "Llegados a este punto, ya deberías tener una idea mucho más clara de cómo cifrar y respaldar tu información crítica sin depender únicamente de la suerte.",
        "Has visto que no se trata de una única herramienta mágica, sino de combinar principios (confianza cero, verificación continua, evitar puntos únicos de fallo) con soportes distintos (nube, papel, dispositivos, memoria).",
        "Empezar siempre es la parte más difícil. Pero una vez que defines tu esquema y lo practicas un par de veces, se vuelve un hábito manejable.",
        "Ahora que cuentas con una base sólida de cifrado y respaldo, estás mucho mejor equipada o equipado para entrar en este bosque oscuro llamado blockchain y sobrevivir en él a largo plazo."
      ]
    }
  ]
};

export default CAP9;
